import { AsKTimeOrNull, kEdTblSelectionMode, kHTMLEditableColType } from "./kEditableTableTypes";
import ks from "../ReUsableTS/kBrowserShared";
import LNG from "../ReUsableTS/kLNG";
import { kEditableTableRows } from "./kEditableTableRow";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableColumn } from "./kEditableColumn";
import type { kEditableTable } from "./kEditableTable";
import { kEditableCellSelectionExpand } from "./kEditableCellSelectionExpand";
import { kEditableCellSelectionQuery } from "./kEditableCellSelectionQuery";
import type { kTime } from "../ReUsableTS/kBrowserObjects";

// kEditableCellSelection, moved out of kEditableTable.ts (size split, 2026-10-20); re-exported from kEditableTable.ts.

export class kEditableCellSelection{

    public SelectionMode:kEdTblSelectionMode = kEdTblSelectionMode.NoSelection;
    
    public RowIndexStart:number = 0;
    public RowIndexEnd:number = 0

    public ColindexStart:number = 0;
    public ColIndexEnd:number = 0;

    public _AnchorRow: number = 0;
    public _AnchorCol: number = 0;

    public SelectedRows: number = 1;
    public SelectedCols: number = 1;
    private _PrevSelectedCells: kEditableTableCell[] = [];
    public ClearPrev = (): void => kEditableCellSelectionExpand.ClearPrev(this);
    public SetSingleCell = (CurrCell:kEditableTableCell): void => kEditableCellSelectionExpand.SetSingleCell(this, CurrCell);
    public SetSingleRowSelected = (CurrentRow:kEditableTableRow): void => kEditableCellSelectionExpand.SetSingleRowSelected(this, CurrentRow);
    public SetSingleColSelected = (CurrCol:kEditableColumn): void => kEditableCellSelectionExpand.SetSingleColSelected(this, CurrCol);
    public ClearTextSelection = (): void => kEditableCellSelectionExpand.ClearTextSelection(this);
    public SelectAll = (): void => kEditableCellSelectionExpand.SelectAll(this);
    private _CheckSingleCell = (): void => kEditableCellSelectionExpand._CheckSingleCell(this);
    public ExpandRight = (): void => kEditableCellSelectionExpand.ExpandRight(this);
    /** ExpandRight from a SingleCell selection. */
    private _ExpandRightFromCell = (): void => kEditableCellSelectionExpand._ExpandRightFromCell(this);
    /** ExpandRight when the range already grows rightwards. */
    private _ExpandRightWiden = (): void => kEditableCellSelectionExpand._ExpandRightWiden(this);
    /** ExpandRight when the range grows leftwards — shrink it, or flip direction. */
    private _ExpandRightFromLeft = (): void => kEditableCellSelectionExpand._ExpandRightFromLeft(this);
    /** ExpandRight from a whole-column selection. */
    private _ExpandRightFromColumn = (): void => kEditableCellSelectionExpand._ExpandRightFromColumn(this);
    /** ExpandRight across an already-multi-column selection. */
    private _ExpandRightMoreCols = (): void => kEditableCellSelectionExpand._ExpandRightMoreCols(this);
    /** The two ExpandRight column-grow shapes in one shell: grow ColIndexEnd, revert past Max, optionally set the mode.
        Max is -1 only when ParentTable is null (the old early return); else Columns.Items.length (+0 whole-column, -1 multi-col). */
    private _ExpandRightCols = (Max: number, SetMode: kEdTblSelectionMode | null, Label: string): void => kEditableCellSelectionExpand._ExpandRightCols(this, Max, SetMode, Label);
    public ExpandUp = (): void => kEditableCellSelectionExpand.ExpandUp(this);
    /** ExpandUp from a SingleCell selection. */
    private _ExpandUpFromCell = (): void => kEditableCellSelectionExpand._ExpandUpFromCell(this);
    /** ExpandUp when the range grows downwards — shrink it, or flip direction. */
    private _ExpandUpFromDown = (): void => kEditableCellSelectionExpand._ExpandUpFromDown(this);
    /** ExpandUp when the range already grows upwards. */
    private _ExpandUpHeighten = (): void => kEditableCellSelectionExpand._ExpandUpHeighten(this);
    /** ExpandUp from a whole-row selection. */
    private _ExpandUpFromRow = (): void => kEditableCellSelectionExpand._ExpandUpFromRow(this);
    /** ExpandUp across a multi-row selection that grew downwards. */
    private _ExpandUpShrinkDown = (): void => kEditableCellSelectionExpand._ExpandUpShrinkDown(this);
    public ExpandDown = (): void => kEditableCellSelectionExpand.ExpandDown(this);
    /** ExpandDown from a SingleCell selection. */
    private _ExpandDownFromCell = (): void => kEditableCellSelectionExpand._ExpandDownFromCell(this);
    /** ExpandDown when the range already grows downwards. */
    private _ExpandDownHeighten = (): void => kEditableCellSelectionExpand._ExpandDownHeighten(this);
    /** ExpandDown when the range grows upwards — shrink it, or flip direction. */
    private _ExpandDownFromUp = (): void => kEditableCellSelectionExpand._ExpandDownFromUp(this);
    /** ExpandDown from a whole-row selection. */
    private _ExpandDownFromRow = (): void => kEditableCellSelectionExpand._ExpandDownFromRow(this);
    /** The two ExpandDown seed shapes in one shell: below the last row, grow it, and - when
     * Mode is given - mark 2 rows and set the mode. _ExpandDownHeighten passes null to keep
     * its grow-only shape. The KGlobalErrorHandler label is always the caller's own.
     */
    private _ExpandDownSeed = (Mode: kEdTblSelectionMode | null, Label: string): void => kEditableCellSelectionExpand._ExpandDownSeed(this, Mode, Label);
    /** ExpandDown across a multi-row selection that grew upwards. */
    private _ExpandDownShrinkUp = (): void => kEditableCellSelectionExpand._ExpandDownShrinkUp(this);
    public ExpandLeft = (): void => kEditableCellSelectionExpand.ExpandLeft(this);
    /** ExpandLeft from a SingleCell selection. */
    private _ExpandLeftFromCell = (): void => kEditableCellSelectionExpand._ExpandLeftFromCell(this);
    /** ExpandLeft when the range already grows leftwards. */
    private _ExpandLeftWiden = (): void => kEditableCellSelectionExpand._ExpandLeftWiden(this);
    /** ExpandLeft when the range grows rightwards — shrink it, or flip direction. */
    private _ExpandLeftFromRight = (): void => kEditableCellSelectionExpand._ExpandLeftFromRight(this);
    /** ExpandLeft from a whole-column selection. */
    private _ExpandLeftFromColumn = (): void => kEditableCellSelectionExpand._ExpandLeftFromColumn(this);
    /** ExpandLeft across a multi-column selection that grew leftwards. */
    private _ExpandLeftMoreCols = (): void => kEditableCellSelectionExpand._ExpandLeftMoreCols(this);
    /** The two ExpandLeft column-grow shapes in one shell: grow ColindexStart down to 0, revert past it, optionally set the mode. */
    private _ExpandLeftCols = (SetMode: kEdTblSelectionMode | null, Label: string): void => kEditableCellSelectionExpand._ExpandLeftCols(this, SetMode, Label);
    /** ExpandLeft across a multi-column selection that grew rightwards. */
    private _ExpandLeftFewerCols = (): void => kEditableCellSelectionExpand._ExpandLeftFewerCols(this);
    public SetSelectionByIndexes = (SelectionMode:kEdTblSelectionMode): void => kEditableCellSelectionQuery.SetSelectionByIndexes(this, SelectionMode);
    public ParentTable: kEditableTable | null = null;
    public GetSelectedText = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            let CurrRow: kEditableTableRow;
            let CurrentCell: kEditableTableCell;
            let SelectedText: string = this._CopyHeaderRow();
            let CurrRowText: string = "";
            if (this.ParentTable) {
                for (CurrRow of this.ParentTable.Rows.Items) {
                    CurrRowText = "";
                    for (CurrentCell of CurrRow.Cells) {
                        if (CurrentCell.Cell.classList.contains("SelectedCellSingle") || CurrentCell.Cell.classList.contains("SelectedCellMulti")) {
                            CurrentCell.SetCellReadMode();
                            CurrRowText += kEditableCellSelection._CopyTextForCell(CurrentCell);
                            if (this.SelectionMode === kEdTblSelectionMode.SingleCell) {
                                return CurrRowText;
                            } else {
                                if (this.SelectedCols > 1) {
                                    CurrRowText += "\t";
                                }
                            }
                        }

                    }
                    if (CurrRowText !== "") {
                        SelectedText += CurrRowText + "\n";
                    }
                }
            }
            return SelectedText;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelection.GetSelectedText");
            return "";
        }
    }
    /** Tab-separated column titles + newline, but only for a Select-All copy. */
    private _CopyHeaderRow = (): string => kEditableCellSelectionQuery._CopyHeaderRow(this);

    /** The clipboard text one selected cell contributes to the copied TSV. */
    private static _CopyTextForCell = (CurrentCell: kEditableTableCell): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (CurrentCell.CurrentValue === null) { return ""; }
            switch (CurrentCell.ColDef.Type) {
                case kHTMLEditableColType.Date: {
                    const CopyDate: kTime | null = AsKTimeOrNull(CurrentCell.CurrentValue);
                    if (CopyDate !== null) { return CopyDate.ToISODate(); }
                    return "";
                }
                case kHTMLEditableColType.RichHtml:
                    // Plain text with whitespace collapsed so the copied TSV stays
                    // rectangular for Excel; an inserted diagram copies as
                    // "[Diagram]" instead of vanishing.
                    return ks.PlainTextFromHTMLWithDiagramToken(ks.Nz(CurrentCell.CurrentValue), "[" + LNG.DIAGRAM + "]").replace(/[\t\r\n]+/g, " ").trim();
                default:
                    return String(CurrentCell.CurrentValue);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelection._CopyTextForCell");
            return "";
        }
    };
    public GetTopLeftCell = (): kEditableTableCell|null => kEditableCellSelectionQuery.GetTopLeftCell(this);
    /** The top-row cell of the selection at the given column index, or null (no table / empty row). */
    private _TopCell = (ColIndex: number, Label: string): kEditableTableCell | null => kEditableCellSelectionQuery._TopCell(this, ColIndex, Label);
    public GetTopRigthCell = (): kEditableTableCell|null => kEditableCellSelectionQuery.GetTopRigthCell(this);
    public SelectionChangeMulti = (NewSelectedCell:kEditableTableCell): void => kEditableCellSelectionQuery.SelectionChangeMulti(this, NewSelectedCell);
    /** Commits any open inline editor and closes the row context menu. */
    private _CloseEditorsBeforeMultiSelect = (): void => kEditableCellSelectionQuery._CloseEditorsBeforeMultiSelect(this);
    /** Derives the extend-direction SelectionMode from anchor vs new cell. */
    private _SetModeFromAnchor = (NewSelectedCell:kEditableTableCell): void => kEditableCellSelectionQuery._SetModeFromAnchor(this, NewSelectedCell);
    public GetSelectedCells = (): kEditableTableCell[] => kEditableCellSelectionQuery.GetSelectedCells(this);
    /** Gathers the cells the current SelectionMode covers. */
    private _CollectSelectedCells = (): kEditableTableCell[] => kEditableCellSelectionQuery._CollectSelectedCells(this);
    /** SingleCell mode — the one cell currently open for editing. */
    private _CollectSingleCell = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectSingleCell(this, SelectedCells);
    /** SingleColumn mode — every row's cell in the anchor column. */
    private _CollectSingleColumn = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectSingleColumn(this, SelectedCells);
    /** The rectangular block-collection spine shared by _CollectColumnRange and
     * _CollectRectangle (finding 6857): push every cell of the inclusive row span crossed
     * with the inclusive column span. RowsFirst preserves each caller's pre-fold iteration
     * order (rectangle row-major, column range column-major) because the order of the public
     * GetSelectedCells array is caller-visible.
     * DOCUMENTED EXCEPTION: pure iteration over ParentTable - no gate/try of its own; both
     * callers keep their KErrorMode gate, try/catch and per-member label. */
    private _CollectBlock = (SelectedCells:kEditableTableCell[], RowsFirst: boolean, RowStart: number, RowEnd: number, ColStart: number, ColEnd: number): void => kEditableCellSelectionQuery._CollectBlock(this, SelectedCells, RowsFirst, RowStart, RowEnd, ColStart, ColEnd);
    /** MultipleCols modes — every row's cell across the selected column span. */
    private _CollectColumnRange = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectColumnRange(this, SelectedCells);
    /** SingleRow mode — every cell of the anchor row. */
    private _CollectSingleRow = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectSingleRow(this, SelectedCells);
    /** MultipleRows / SelectAll modes — every cell of the selected row span. */
    private _CollectRowRange = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectRowRange(this, SelectedCells);
    /** CustomExt modes — the rectangle between anchor and current cell. */
    private _CollectRectangle = (SelectedCells:kEditableTableCell[]): void => kEditableCellSelectionQuery._CollectRectangle(this, SelectedCells);
    public GetSelectedRows = (): kEditableTableRows => kEditableCellSelectionQuery.GetSelectedRows(this);
    private _PaintRangeEdges = (SelectedCells: kEditableTableCell[]): void => kEditableCellSelectionQuery._PaintRangeEdges(this, SelectedCells);
    private _SetSelectionEdges = (CurrCell: kEditableTableCell, Top: boolean, Right: boolean, Bottom: boolean, Left: boolean): void => kEditableCellSelectionQuery._SetSelectionEdges(this, CurrCell, Top, Right, Bottom, Left);
    private _ClearSelectionEdges = (CurrCell: kEditableTableCell): void => kEditableCellSelectionQuery._ClearSelectionEdges(this, CurrCell);
    private _ClearDeselectedCells = (SelectedIds: Set<number>): void => kEditableCellSelectionQuery._ClearDeselectedCells(this, SelectedIds);
    private _PaintMultiSelection = (SelectedCells: kEditableTableCell[]): void => kEditableCellSelectionQuery._PaintMultiSelection(this, SelectedCells);
    private _SyncActiveDescendant = (SelectedCells: kEditableTableCell[]): void => kEditableCellSelectionQuery._SyncActiveDescendant(this, SelectedCells);
    public UpdateSelectionStyles = (): void => kEditableCellSelectionQuery.UpdateSelectionStyles(this);
    public SelectLastCell = (): void => kEditableCellSelectionQuery.SelectLastCell(this);
    public SelectFirstCell = (): void => kEditableCellSelectionQuery.SelectFirstCell(this);
    public SelectCellByIndexes = (RowIndex:number, ColIndex:number): void => kEditableCellSelectionQuery.SelectCellByIndexes(this, RowIndex, ColIndex);

}
